import { Assets, Rectangle, Texture } from 'pixi.js';

// Alpha minimum to consider a pixel "content" (out of 255). Avoids latching
// onto faint anti-aliasing or soft shadows around an otherwise empty border.
const CONTENT_ALPHA_THRESHOLD = 10;

export type LoadSpriteSheetOpts = {
  // If true (default), computeUnionTrim trims each cell VERTICALLY to a robust
  // ground line (full cell width kept). With anchor 0.5,1 the character then
  // lands on the same ground line + same (cell-center) X across every sheet,
  // pose-independent, no per-sheet offset tuning. One rect for the whole sheet
  // (not per-frame) so the sprite doesn't pulse between frames.
  //
  // Set false for sheets where you want the raw cell anchor instead (rare).
  autoTrim?: boolean;
};

// Loads a sprite sheet PNG and slices it into a flat array of frame textures.
// Order: row-major, left-to-right, top-to-bottom (frame 0 = top-left).
// Frames are assumed to be uniform (sheet width / cols, sheet height / rows).
export async function loadSpriteSheetFrames(
  url: string,
  cols: number,
  rows: number,
  opts?: LoadSpriteSheetOpts,
): Promise<Texture[]> {
  const sheet = await Assets.load<Texture>(url);
  const frameW = sheet.width / cols;
  const frameH = sheet.height / rows;

  const trim = (opts?.autoTrim ?? true)
    ? computeUnionTrim(sheet, cols, rows, frameW, frameH)
    : null;

  const frames: Texture[] = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const baseX = c * frameW;
      const baseY = r * frameH;
      const rect = trim
        ? new Rectangle(baseX + trim.x, baseY + trim.y, trim.w, trim.h)
        : new Rectangle(baseX, baseY, frameW, frameH);
      frames.push(new Texture({ source: sheet.source, frame: rect }));
    }
  }
  return frames;
}

// A "ground" row must have at least this fraction of the cell width opaque
// (across the per-cell-position union of all frames). Filters out faint
// anti-aliasing and a couple of stray VFX pixels that would otherwise be
// mistaken for the floor and drag the anchor down.
const GROUND_MIN_COVERAGE = 0.05;

// Computes the trim rect (cell-local px). Key model decision:
//   - VERTICAL is content-based: bottom = a ROBUST ground line (lowest row
//     that's actually "solid", not a faint AA fringe or a couple of stray VFX
//     dots below the feet), top = topmost content. Vertical needs this because
//     the FLOOR is the only cross-sheet invariant (gravity); cell vertical
//     padding varies per sheet.
//   - HORIZONTAL is NOT content-based: we keep the FULL cell width and anchor
//     at the cell center. There is no horizontal physical invariant - any
//     pixel-derived "character center" (extent / centroid / feet) shifts with
//     pose (arm/leg extended in the facing direction), which is exactly why
//     every action sheet drifted the same way vs idle. Trusting the artist's
//     in-cell framing (character drawn centered in its cell) makes all sheets
//     align by construction, pose-independent. If a sheet ISN'T cell-centered
//     that's an asset issue -> per-sheet timings.offsetX (the documented knob).
//
// Returns null on: CORS-tainted canvas, no 2D context, bad resource, or a
// fully transparent / no-solid-row sheet. Caller then falls back to raw cells.
function computeUnionTrim(
  sheet: Texture,
  cols: number,
  rows: number,
  cellW: number,
  cellH: number,
): { x: number; y: number; w: number; h: number } | null {
  // Pixi v8: source.resource is the underlying ImageBitmap / HTMLImageElement /
  // canvas. All of these are valid CanvasImageSource for drawImage. If the
  // shape changes in a future Pixi version, the try/catch below falls through.
  const resource = sheet.source.resource as CanvasImageSource | undefined;
  if (!resource) return null;

  const sheetW = Math.floor(sheet.width);
  const sheetH = Math.floor(sheet.height);

  const canvas = document.createElement('canvas');
  canvas.width = sheetW;
  canvas.height = sheetH;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  let data: Uint8ClampedArray;
  try {
    ctx.drawImage(resource, 0, 0);
    data = ctx.getImageData(0, 0, sheetW, sheetH).data;
  } catch {
    return null;
  }

  const cellWPx = Math.floor(cellW);
  const cellHPx = Math.floor(cellH);

  // For each cell-local row, count how many columns are opaque in the UNION of
  // all frames (a column counts if ANY frame has content there). We only need
  // this vertical profile - horizontal is the full cell.
  const minSolid = Math.max(3, Math.round(cellWPx * GROUND_MIN_COVERAGE));
  let minY = -1;
  let feetY = -1;
  for (let y = 0; y < cellHPx; y++) {
    let count = 0;
    for (let x = 0; x < cellWPx; x++) {
      let opaque = false;
      for (let r = 0; r < rows && !opaque; r++) {
        for (let c = 0; c < cols; c++) {
          const srcIdx =
            ((Math.floor(r * cellH) + y) * sheetW + Math.floor(c * cellW) + x) * 4 + 3;
          if ((data[srcIdx] ?? 0) > CONTENT_ALPHA_THRESHOLD) {
            opaque = true;
            break;
          }
        }
      }
      if (opaque) count++;
    }
    if (count === 0) continue;
    if (minY < 0) minY = y;
    if (count >= minSolid) feetY = y; // lowest row that's a real mass
  }
  if (minY < 0 || feetY < 0) return null;

  // Full cell width, vertical trimmed to [top .. ground line]. anchor 0.5,1
  // then maps the CELL center + ground line - identical reference for every
  // sheet of the character regardless of pose.
  return {
    x: 0,
    y: minY,
    w: cellWPx,
    h: feetY - minY + 1,
  };
}
